import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Hexagon, Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, X, Plus, CalendarDays, Flag, CalendarCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { holidaysApi, dashboardApi } from '../lib/api';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import { format } from 'date-fns';
import {
  parsePlace, holidayMatchesPlace, placesIn, placeLabel as sharedPlaceLabel,
} from '../lib/holidayPlaces';
import AddCompanyDayForm from '../components/AddCompanyDayForm';
import HolidayDayList from '../components/HolidayDayList';
import { REGION_LABELS } from '../lib/regions';
import { removeQuestion, removedMessage, restoredMessage } from '../lib/holidayActions';

// ⚠️ THE REGION NAMES COME FROM `lib/regions.js` NOW. This file used to declare its own,
// spelling it "England and Wales" while Settings and Locations said "England & Wales" —
// the same region called two things depending on which screen you were on. Four copies
// existed on 13/08/2026; two of them had already drifted.

// Sentinel for the filter — not a region, so it can't collide with a real region code.
const COMPANY_DAYS = '__company__';

// The place rule lives in lib/holidayPlaces.js so it can be checked without a browser
// (node src/lib/holidayPlaces.checks.mjs). See the warning at the top of that file.

const HolidaysPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  // The once-a-year "have you looked at these?" review — step 5, 08/08/2026.
  // `null` while loading; `needs_review: false` once somebody has confirmed this year.
  const [review, setReview] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  // Which region's holidays to LIST — a multi-region company legitimately has the same
  // holiday several times, one row per region, which reads as duplicates without this.
  const [listRegion, setListRegion] = useState('');
  // Bank holidays this company has taken off its calendar. Shown so the bin isn't a
  // one-way door — being able to put one back is the whole reason opt-outs exist
  // rather than deletes.
  const [optedOut, setOptedOut] = useState([]);
  // Code -> display name, from the same endpoint every country picker uses. Without it
  // the filter would have to show "DZ" and nobody would know what that meant.
  const [countryNames, setCountryNames] = useState({});

  const handleRestoreHoliday = async (id, name) => {
    try {
      await holidaysApi.restore(id);
      toast.success(restoredMessage(name));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not restore that holiday');
    }
  };

  // Every place this company actually has holidays for. Most customers have one and
  // never see the filter; Anthropic has London, Edinburgh, Belfast — and now Algiers.
  const placesPresent = React.useMemo(() => placesIn(holidays), [holidays]);

  const countriesPresent = React.useMemo(
    () => [...new Set(placesPresent.map((p) => p.countryCode).filter(Boolean))],
    [placesPresent]);

  // Only name the country when there is more than one — "Scotland" is clearer than
  // "United Kingdom — Scotland" for the British customers who are nearly all of them.
  // ⚠️ THE WORDS FOR A PLACE MOVED TO `lib/holidayPlaces.js` on 13/08/2026, because the
  // add-a-day form now names places too and signup renders the same list.
  const placeLabel = React.useCallback(
    (place) => sharedPlaceLabel(place, countryNames, countriesPresent.length > 1),
    [countryNames, countriesPresent]);

  const sortedPlaces = React.useMemo(
    () => [...placesPresent].sort((a, b) => placeLabel(a).localeCompare(placeLabel(b))),
    [placesPresent, placeLabel]);

  const hasCompanyDays = React.useMemo(
    () => holidays.some((h) => h.is_custom), [holidays]);

  /**
   * Filtering by place means "what would someone working here see" — the same question
   * `holidays_util.applicable_holidays` answers on the server.
   *
   * ⚠️ A COMPANY DAY BELONGS IN EVERY VIEW. My first version filtered on
   * `h.region === listRegion`, which quietly dropped them: a day the whole company
   * takes off has no region, so picking "England and Wales" hid it — and an admin
   * would reasonably conclude it hadn't saved.
   *
   * ⚠️ BUT A NULL REGION DOES NOT MEAN EVERY COUNTRY. That was the second half of the
   * bug: Algeria's holidays have no region, so `|| !h.region` showed them under
   * England and Wales. Country is checked first, and only when both sides have one —
   * legacy rows predating the country column must not vanish.
   */
  const visibleHolidays = React.useMemo(() => {
    if (!listRegion) return holidays;
    if (listRegion === COMPANY_DAYS) return holidays.filter((h) => h.is_custom);
    const place = parsePlace(listRegion);
    return holidays.filter((h) => holidayMatchesPlace(h, place));
  }, [holidays, listRegion]);

  useEffect(() => {
    fetchData();
  // Runs once on mount. `fetchData` is recreated every render, so listing it would re-fetch endlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    // Country names change about once a decade — fetched once, not per year.
    holidaysApi.getCountries()
      .then((r) => setCountryNames(
        Object.fromEntries((r.data.countries || []).map((c) => [c.code, c.name]))))
      .catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [holidaysRes, approvalsRes, optedOutRes, reviewRes] = await Promise.all([
        holidaysApi.list(selectedYear),
        dashboardApi.getPendingApprovals(),
        // Best-effort: an admin-only list shouldn't stop an employee seeing the page.
        holidaysApi.listOptedOut().catch(() => ({ data: [] })),
        // Same: the review banner failing must not take the page down with it.
        holidaysApi.getReview().catch(() => ({ data: null })),
      ]);

      setHolidays(holidaysRes.data);
      setPendingCount(approvalsRes.data.length);
      setOptedOut(optedOutRes.data || []);
      setReview(reviewRes.data);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const confirmReview = async () => {
    setConfirming(true);
    try {
      await holidaysApi.confirmReview();
      // ⚠️ RE-READ RATHER THAN ASSUME. Setting `needs_review: false` locally would hide
      // the banner even if the save had failed silently — and "the screen says one thing
      // while the server holds another" is the exact fault this whole workstream has
      // spent two days removing.
      const fresh = await holidaysApi.getReview().catch(() => null);
      if (fresh) setReview(fresh.data);
      toast.success(`Thanks — we'll stop asking about ${review?.year || 'this year'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save that');
    } finally {
      setConfirming(false);
    }
  };

  // `fetchUKHolidays` / `handleImportUK` lived here to drive the "Import UK Bank
  // Holidays" panel. Both are gone with it (02/08/2026): bank holidays are maintained
  // centrally and topped up automatically, so a customer importing them was busywork
  // — and the preview fetch was hitting the API on every page load to fill a panel
  // nobody needed. The endpoints still exist for the platform's own use.

  /**
   * ⚠️ THIS HARD-CODED `country_code: 'GB'` UNTIL 13/08/2026, AND IT LOST ROB'S DAY.
   * He added "Hippy Day" to an Algerian company and it never appeared: saved correctly,
   * stamped GB because the form said so, then filtered straight back out of his own list
   * because a GB day applies to nobody in Algeria. The admin was never asked and never
   * chose. The form asks now — see `AddCompanyDayForm`.
   */
  const handleAddHoliday = async ({ name, date, country_code, region, problem }) => {
    if (problem) { toast.error(problem); return false; }
    try {
      await holidaysApi.create({ name, date, country_code, region });
      toast.success('Added to your calendar');
      setShowAddForm(false);
      fetchData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add that day');
      return false;
    }
  };

  /**
   * Two different actions behind one bin icon, and the wording has to match which.
   *
   * A company's OWN day is really deleted. A bank holiday is a PLATFORM row shared by
   * every customer, so it can't be deleted — the server records an opt-out instead and
   * says so. Telling an admin "deleted" when nothing was deleted would be a lie they'd
   * eventually catch.
   */
  const handleDeleteHoliday = async (id, holiday) => {
    // ⚠️ THE WORDS LIVE IN `lib/holidayActions.js` so signup asks the identical question.
    // Two screens describing the same action differently is how an admin ends up unsure
    // whether "deleted" and "removed from your calendar" mean the same thing.
    if (!window.confirm(removeQuestion(holiday))) return;

    try {
      const { data } = await holidaysApi.delete(id);
      toast.success(removedMessage(holiday, data?.message));
      fetchData();
    } catch (error) {
      console.error('Failed to remove holiday:', error);
      toast.error(error.response?.data?.detail || 'Could not remove that holiday');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="font-heading text-2xl font-bold">Public Holidays</h1>
            </div>
            {isAdmin && (
              <Button 
                className="btn-primary"
                onClick={() => setShowAddForm(true)}
                data-testid="add-holiday-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Holiday
              </Button>
            )}
          </div>
        </header>

        <div className="p-6 space-y-6">
          {/* ⚠️ THE ONCE-A-YEAR REVIEW — step 5 of the locations plan, 08/08/2026.
              The design doc asked for a confirmation screen; the CAPABILITY was already
              on this page (opt out, restore, add company days), so what was missing was
              the MOMENT. Nothing ever asked anybody to look, and nothing recorded that
              they had — so the days applied silently and the first sign of a wrong one
              was somebody's balance.

              ⚠️ IT SHOWS THE COUNT PER OFFICE, not a yes/no. A confirmation you cannot
              check is theatre: "8 for London, 10 for Belfast, 14 for Algiers" is
              something an admin can disagree with at a glance.

              Admin only — it silences a warning about how much leave everyone gets. */}
          {isAdmin && review?.needs_review && !dismissed && (
            <div className="card-hive p-5 border-primary/40">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                  <CalendarCheck className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-heading font-bold text-lg">
                    Check your public holidays for {review.year}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    These are the days your team automatically gets off. Remove any you
                    don't close for, and add your own company days.
                  </p>

                  <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {(review.places || []).map((p) => (
                      <div key={p.location_id || p.name} className="rounded-lg bg-secondary/40 px-3 py-2">
                        <div className="text-xs text-muted-foreground truncate">{p.name}</div>
                        <div className="text-base font-medium">
                          {p.count} {p.count === 1 ? 'day' : 'days'}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button className="btn-primary" disabled={confirming} onClick={confirmReview}>
                      {confirming ? 'Saving…' : 'Looks right'}
                    </Button>
                    {/* ⚠️ DISMISS IS FOR THIS VISIT ONLY, and deliberately not saved.
                        Persisting "later" would silence the one prompt that exists,
                        which is how a skippable nudge becomes a nudge nobody ever
                        sees. The Perfection Box keeps carrying it until it is
                        actually confirmed. */}
                    <Button variant="outline" onClick={() => setDismissed(true)}>
                      I'll check later
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* WHO MAINTAINS THESE.
              There used to be an "Import UK Bank Holidays" panel here with a big blue
              button. It's gone: since 02/08/2026 bank holidays are maintained by
              HolidayHive centrally and topped up automatically before they run out, so
              a button implying it's the customer's job was telling them something
              untrue. What a customer CAN do is add their own days and remove ones they
              don't observe — which is what this panel now says. */}
          {isAdmin && (
            <div className="card-hive p-6">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Flag className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="max-w-2xl">
                    <h2 className="font-heading font-bold text-lg">Bank holidays are kept up to date for you</h2>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      We load the official bank holidays for every region your locations are
                      in, and keep them topped up — you don't need to import anything.
                      Add your own company days below, and use the bin to take one off your
                      calendar if you don't close for it.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowAddForm((v) => !v)}
                  className="btn-primary whitespace-nowrap"
                  data-testid="add-company-day-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add a company day
                </Button>
              </div>
            </div>
          )}

          {/* Add Holiday Form */}
          {showAddForm && (
            <div className="card-hive p-6">
              <h3 className="font-heading font-bold mb-4">Add a company day</h3>
              {/* ⚠️ THE SAME FORM SIGNUP USES — `components/AddCompanyDayForm.jsx`.
                  Two add-a-day forms is how one of them ends up hard-coding a country
                  again. */}
              <AddCompanyDayForm
                places={sortedPlaces}
                countryNames={countryNames}
                onAdd={handleAddHoliday}
                onCancel={() => setShowAddForm(false)}
              />
            </div>
          )}

          {/* Holidays List */}
          <div className="card-hive overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-heading font-bold">Your Holidays ({selectedYear})</h2>
              <div className="flex items-center gap-3">
                {/* A multi-region company legitimately has the same holiday several
                    times — one row per region. Without a filter that reads as
                    duplicates, which is exactly how this page looked before. */}
                {(sortedPlaces.length > 1 || hasCompanyDays) && (
                  <select
                    value={listRegion}
                    onChange={(e) => setListRegion(e.target.value)}
                    className="input-hive text-sm py-1"
                    data-testid="holiday-region-filter"
                  >
                    <option value="">Everything ({holidays.length})</option>
                    {sortedPlaces.map((p) => (
                      <option key={p.key} value={p.key}>{placeLabel(p)}</option>
                    ))}
                    {hasCompanyDays && (
                      <option value={COMPANY_DAYS}>Your company days</option>
                    )}
                  </select>
                )}
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {visibleHolidays.length} holiday{visibleHolidays.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              /* ⚠️ THE SHARED LIST — `components/HolidayDayList.jsx`. Signup shows the
                 same days with the same actions and the same words (Rob's option A,
                 13/08/2026). The fetching, the confirm dialog and the toasts stay here,
                 because those are the parts the two screens legitimately differ on. */
              <HolidayDayList
                holidays={visibleHolidays}
                optedOut={optedOut}
                canEdit={isAdmin}
                onRemove={(h) => handleDeleteHoliday(h.id, h)}
                onRestore={(h) => handleRestoreHoliday(h.id, h.name)}
                emptyMessage="No public holidays configured"
                countryNames={countryNames}
                multiCountry={countriesPresent.length > 1}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default HolidaysPage;
